import React, { memo, useEffect, useState, useCallback } from "react";
import { VisibleGroupsContext } from ".";
import useData from "../../hooks/useData";
import useMessage, { MESSAGE_TYPES } from "../../hooks/useMessage";
import binSearch from "../../utils/binSearch";
import debounce from "../../utils/debounce";

const updateRange = debounce(({ groupRefs, frame, setVisibleRange }) => {
	if (!groupRefs.current) return;

	if (!frame.inViewPort) {
		return setVisibleRange({ start: -1, end: -1 });
	}

	const start = binSearch(groupRefs.current, (left, mid, right) => {
		let { top, height } =
			groupRefs.current?.[mid]?.getBoundingClientRect() ?? {};
		top -= frame.start;

		if (top >= 0) {
			return {
				found: top + height <= window.innerHeight,
				nextLeft: left,
				nextRight: mid,
			};
		}
		return { found: false, nextLeft: mid + 1, nextRight: right };
	});

	const end = binSearch(groupRefs.current, (left, mid, right) => {
		let { top, height } =
			groupRefs.current?.[mid]?.getBoundingClientRect() ?? {};
		top -= frame.start;

		if (top >= 0) {
			if (top + height <= window.innerHeight) {
				return {
					found: true,
					nextLeft: mid + 1,
					nextRight: right,
				};
			}
			return {
				found: false,
				nextLeft: left,
				nextRight: mid,
			};
		}
		return { found: false, nextLeft: mid + 1, nextRight: right };
	});

	setVisibleRange({ start, end });
}, 20);

const VisibleGroupsManager = memo(({ children }) => {
	const { groupRefs, containerRef } = useData();

	const [frame, setFrame] = useState({
		start: 0,

		top: 0,
		height: 0,

		windowInnerHeight: 0,

		inViewPort: false,
	});

	const handleParentStatsUpdate = useCallback(
		({ widget, windowInnerHeight }) => {
			if (!widget) return;
			const { top, height } = widget.boundingClientRect;
			setFrame({
				start: widget.visibleFrameStart,

				top,
				height,
				end: widget.visibleFrameEnd,

				windowInnerHeight,

				inViewPort: !(top + height <= 0 || top > windowInnerHeight),
			});
		},
		[]
	);

	const [visibleRange, setVisibleRange] = useState({ start: -1, end: -1 });

	useMessage(MESSAGE_TYPES.UPDATE_PARENT_STATS, handleParentStatsUpdate);

	const handleScroll = useCallback(
		() => updateRange({ groupRefs, frame, setVisibleRange }),
		[frame, groupRefs]
	);

	useEffect(() => {
		updateRange({ groupRefs, frame, setVisibleRange });
	}, [groupRefs, frame, setVisibleRange]);

	useEffect(() => {
		if (!containerRef.current) return;
		containerRef.current.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [containerRef, handleScroll]);

	return (
		<VisibleGroupsContext.Provider
			value={{ ...visibleRange, frame, handleScroll }}
		>
			{children}
		</VisibleGroupsContext.Provider>
	);
});
export default VisibleGroupsManager;
